/**
 * Screen share (Go Live) and camera video: capture, scaling and the small
 * pure rules the engine and the screens share
 * (docs/decisions/2026-10-29-screen-share-and-video.md, §2, §3).
 *
 * Traffic first: VP8 only, one encoding per source (no simulcast), held
 * under the caps signed into the voice token (`VoicePublishCaps` from
 * `published`) with `maxBitrate`, `maxFramerate` and `scaleResolutionDownBy`;
 * a video reaches only the people who have it on screen (`visible`), and a
 * screen only those who also clicked Watch.
 */
import {
  VOICE_STREAM_AUDIO_SLOTS,
  VOICE_VIDEO_SLOTS,
  mediaBoxWidth,
  mediaScaleDown,
  type Pid,
  type VoiceMediaCaps,
  type VoiceMediaSource,
  type VoicePublishCaps,
  type VoiceVideoRef,
} from '@neosocial/protocol';
import { isMobileApp } from '../lib/config';

/** §2.2's defaults: what applies when neither the token nor the live config says. */
export const DEFAULT_MEDIA_CAPS: VoiceMediaCaps = {
  screen_kbps: 1500,
  screen_width: 1280,
  screen_height: 720,
  screen_fps: 30,
  screen_audio_kbps: 64,
  camera_kbps: 350,
  camera_width: 640,
  camera_height: 360,
  camera_fps: 24,
  streams_per_channel: 2,
  cameras_per_channel: 4,
};

/** The most `visible` entries comms-voice reads (§4.2). */
export const VISIBLE_MAX = 16;
/** `visible` changes wait this long, so a scroll or a resize sends one message (§2.1 rule 3). */
export const VISIBLE_DEBOUNCE_MS = 250;

/** The caps of a publication of `source`, from the console's caps (what `published` would say). */
export function publishCapsOf(source: VoiceMediaSource, c: VoiceMediaCaps): VoicePublishCaps {
  return source === 'screen'
    ? {
        max_kbps: c.screen_kbps,
        max_width: c.screen_width,
        max_height: c.screen_height,
        max_fps: c.screen_fps,
        audio_kbps: c.screen_audio_kbps,
      }
    : {
        max_kbps: c.camera_kbps,
        max_width: c.camera_width,
        max_height: c.camera_height,
        max_fps: c.camera_fps,
      };
}

/**
 * The console's live `limits.*` over the token's caps (a change reaches the
 * next publication), else §2.2's defaults. Bad values do not count.
 */
export function mediaCapsFrom(
  settings: Record<string, unknown> | null | undefined,
  token: VoiceMediaCaps | null | undefined,
): VoiceMediaCaps {
  const base = { ...DEFAULT_MEDIA_CAPS, ...(token ?? {}) };
  const num = (k: string, fallback: number) => {
    const v = settings?.[k];
    return typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : fallback;
  };
  const screenH = num('limits.stream_max_height', base.screen_height);
  const cameraH = num('limits.camera_max_height', base.camera_height);
  return {
    screen_kbps: num('limits.stream_bitrate_kbps', base.screen_kbps),
    screen_height: screenH,
    screen_width: settings?.['limits.stream_max_height']
      ? mediaBoxWidth(screenH)
      : base.screen_width,
    screen_fps: num('limits.stream_max_fps', base.screen_fps),
    screen_audio_kbps: num('limits.stream_audio_kbps', base.screen_audio_kbps),
    camera_kbps: num('limits.camera_bitrate_kbps', base.camera_kbps),
    camera_height: cameraH,
    camera_width: settings?.['limits.camera_max_height']
      ? mediaBoxWidth(cameraH)
      : base.camera_width,
    camera_fps: num('limits.camera_max_fps', base.camera_fps),
    streams_per_channel: num('limits.streams_per_channel', base.streams_per_channel),
    cameras_per_channel: num('limits.cameras_per_channel', base.cameras_per_channel),
  };
}

/* ------------------------------ the share sheet ------------------------------ */

/** The share sheet's presets (§4.7): 480p or 720p, 15 or 30 fps. */
const HEIGHTS = [480, 720];
const RATES = [15, 30];

/**
 * The choices up to `cap`: the presets at or under it, and the cap itself
 * when the console raised it above them (it never offers more than the cap).
 */
export function choicesUpTo(presets: readonly number[], cap: number): number[] {
  const out = presets.filter((p) => p <= cap);
  if (!out.length || cap > presets[presets.length - 1]!) out.push(cap);
  return out;
}
export const heightChoices = (cap: number) => choicesUpTo(HEIGHTS, cap);
export const rateChoices = (cap: number) => choicesUpTo(RATES, cap);

/** The sheet's starting pick: 720p30 where the caps allow it (Discord's default), else the most they do. */
export function defaultPick(choices: number[], preferred: number): number {
  const under = choices.filter((c) => c <= preferred);
  return under.length ? under[under.length - 1]! : choices[0]!;
}

/* ------------------------------- capability ------------------------------- */

type MediaDevicesLike = Partial<Pick<MediaDevices, 'getDisplayMedia' | 'getUserMedia'>> & {
  getSupportedConstraints?: () => Record<string, boolean | undefined>;
};

function devices(): MediaDevicesLike | undefined {
  return typeof navigator === 'undefined'
    ? undefined
    : (navigator.mediaDevices as unknown as MediaDevicesLike | undefined);
}

/**
 * `getDisplayMedia` exists here (phones and tablets have none; the screens
 * also hide Share there). Never in the phone app: its WebView can't share a
 * screen, whatever it reports.
 */
export function displayCaptureSupported(): boolean {
  if (isMobileApp()) return false;
  return typeof devices()?.getDisplayMedia === 'function';
}

/** A camera can be opened here at all. */
export function cameraSupported(): boolean {
  return typeof devices()?.getUserMedia === 'function';
}

/**
 * Can the capture keep the call itself out of a whole-system sound
 * (`restrictOwnAudio`)? Without it, sharing the whole screen's sound on
 * Windows captures the call too; the sheet says so.
 */
export function canRestrictOwnAudio(): boolean {
  try {
    return !!devices()?.getSupportedConstraints?.()['restrictOwnAudio'];
  } catch {
    return false;
  }
}

/** Where a screen's sound can be shared at all (Chromium: a tab's, and the whole system's on Windows). */
export function screenAudioSupported(
  ua = typeof navigator === 'undefined' ? '' : navigator.userAgent,
): boolean {
  return /\b(Chrome|Chromium|Edg)\//.test(ua) && !/\bMobile\b/.test(ua);
}

/* -------------------------------- capture -------------------------------- */

export interface ScreenPick {
  height: number;
  fps: number;
  audio: boolean;
}

/** `getDisplayMedia`'s options (§3): the frame rate capped, sound when asked, our own tab left out. */
export function displayOptions(p: ScreenPick, restrictOwn = canRestrictOwnAudio()) {
  return {
    video: { frameRate: { max: p.fps } },
    audio: p.audio ? (restrictOwn ? { restrictOwnAudio: true } : true) : false,
    systemAudio: p.audio ? 'include' : 'exclude',
    selfBrowserSurface: 'exclude',
    surfaceSwitching: 'include',
  } as DisplayMediaStreamOptions;
}

export interface Captured {
  video: MediaStreamTrack;
  audio: MediaStreamTrack | null;
}

/** Opens the system picker. Rejects when the person cancels it (`NotAllowedError`). */
export async function captureScreen(p: ScreenPick): Promise<Captured> {
  const md = devices();
  if (!md?.getDisplayMedia)
    throw Object.assign(new Error('no getDisplayMedia'), { name: 'NotSupportedError' });
  const s = await md.getDisplayMedia(displayOptions(p));
  const video = s.getVideoTracks()[0];
  if (!video) {
    for (const t of s.getTracks()) t.stop();
    throw Object.assign(new Error('no video track'), { name: 'NotReadableError' });
  }
  // text stays sharp; the frame rate drops first (§3 encoder hints)
  try {
    video.contentHint = 'detail';
  } catch {
    /* read-only in an old engine */
  }
  const audio = p.audio ? (s.getAudioTracks()[0] ?? null) : null;
  for (const t of s.getAudioTracks()) if (t !== audio) t.stop();
  return { video, audio };
}

/** `getUserMedia`'s camera constraints: the box at most, the front camera on a phone. */
export function cameraConstraints(
  caps: VoicePublishCaps,
  deviceId?: string | null,
): MediaTrackConstraints {
  return {
    width: { ideal: caps.max_width, max: Math.max(caps.max_width, caps.max_height) },
    height: { ideal: caps.max_height, max: Math.max(caps.max_width, caps.max_height) },
    frameRate: { ideal: caps.max_fps, max: caps.max_fps },
    ...(deviceId ? { deviceId: { exact: deviceId } } : { facingMode: 'user' }),
  };
}

/** Opens the camera (the chosen one, else the default front one). */
export async function openCamera(
  caps: VoicePublishCaps,
  deviceId?: string | null,
): Promise<MediaStreamTrack> {
  const md = devices();
  if (!md?.getUserMedia) throw Object.assign(new Error('no camera'), { name: 'NotSupportedError' });
  let s: MediaStream;
  try {
    s = await md.getUserMedia({ video: cameraConstraints(caps, deviceId), audio: false });
  } catch (e) {
    // the saved camera is gone: the default one, as the microphone does
    if (!deviceId || (e as Error).name !== 'OverconstrainedError') throw e;
    s = await md.getUserMedia({ video: cameraConstraints(caps, null), audio: false });
  }
  const t = s.getVideoTracks()[0];
  if (!t) throw Object.assign(new Error('no camera track'), { name: 'NotReadableError' });
  try {
    t.contentHint = 'motion';
  } catch {
    /* ignore */
  }
  return t;
}

/* ------------------------------ the encoder ------------------------------ */

/**
 * The box a publication is held to: its caps, or a lower quality picked
 * under them (the share sheet's, or the tile menu's in place). Its bitrate
 * ceiling falls with it, by the pixels and by the square root of the frame
 * rate (fewer frames each carry more), so 480p sends fewer bits, not 720p's
 * bits in fewer pixels (QA D1): 720p30 at 1500 kbps gives 480p30 at 667,
 * 720p15 at 1061 and 480p15 at 472. Never above the caps.
 */
export function qualityBox(
  caps: VoicePublishCaps,
  q: { height: number; fps: number } | null | undefined,
): VoicePublishCaps {
  if (!q) return caps;
  const h = Math.min(q.height, caps.max_height);
  const w = Math.min(mediaBoxWidth(h), caps.max_width);
  const fps = Math.min(q.fps, caps.max_fps);
  const area =
    caps.max_width > 0 && caps.max_height > 0 ? (w * h) / (caps.max_width * caps.max_height) : 1;
  const rate = caps.max_fps > 0 ? fps / caps.max_fps : 1;
  const kbps = caps.max_kbps * Math.min(1, area) * Math.sqrt(Math.min(1, rate));
  return {
    ...caps,
    max_height: h,
    max_width: w,
    max_fps: fps,
    max_kbps: Math.min(caps.max_kbps, Math.round(kbps)),
  };
}

/**
 * The one encoding of a publication (no simulcast): the caps of `published`
 * on the sender, the capture scaled into the box either way round
 * (`mediaScaleDown`), never up.
 */
export function encodingFor(
  settings: { width?: number; height?: number },
  caps: VoicePublishCaps,
): { maxBitrate: number; maxFramerate: number; scaleResolutionDownBy: number } {
  const w = settings.width ?? caps.max_width;
  const h = settings.height ?? caps.max_height;
  const scale = w > 0 && h > 0 ? mediaScaleDown(w, h, caps.max_width, caps.max_height) : 1;
  return {
    maxBitrate: Math.round(caps.max_kbps * 1000),
    maxFramerate: caps.max_fps,
    scaleResolutionDownBy: Math.round(scale * 1000) / 1000,
  };
}

/** VP8 and its RTX only (§3): one codec everywhere, and smaller offers. */
export function vp8Only<T extends { mimeType: string }>(codecs: readonly T[]): T[] {
  const vp8 = codecs.filter((c) => /^video\/vp8$/i.test(c.mimeType));
  const rtx = codecs.filter((c) => /^video\/rtx$/i.test(c.mimeType));
  return vp8.length ? [...vp8, ...rtx] : [...codecs];
}

/**
 * Stereo for streams' sound (§4.1): `stereo=1;sprop-stereo=1` on the Opus
 * fmtp of the audio m-lines named in `mids`, so a stereo stream plays in
 * stereo. Other sections are left as they are.
 */
export function withStereo(sdp: string, mids: readonly string[]): string {
  if (!mids.length) return sdp;
  const eol = sdp.includes('\r\n') ? '\r\n' : '\n';
  const parts = sdp.split(/(?=\r?\nm=)/);
  return parts
    .map((sec) => {
      const mid = /\r?\na=mid:([^\r\n]+)/.exec(sec)?.[1];
      if (!mid || !mids.includes(mid) || !/^\r?\nm=audio/.test(sec)) return sec;
      const pt = /\r?\na=rtpmap:(\d+) opus\/48000/i.exec(sec)?.[1];
      if (!pt) return sec;
      const fmtp = new RegExp(`(\\r?\\na=fmtp:${pt} )([^\\r\\n]*)`);
      if (fmtp.test(sec))
        return sec.replace(fmtp, (_m, head: string, params: string) => {
          const kept = params
            .split(';')
            .filter((p) => p && !/^(stereo|sprop-stereo)=/.test(p.trim()));
          return head + [...kept, 'stereo=1', 'sprop-stereo=1'].join(';');
        });
      return sec.replace(
        new RegExp(`(\\r?\\na=rtpmap:${pt} [^\\r\\n]*)`),
        `$1${eol}a=fmtp:${pt} stereo=1;sprop-stereo=1`,
      );
    })
    .join('');
}

/** The audio m-lines with `a=recvonly` in `sdp` whose mid is not in `known` (the stream-sound slots). */
export function newRecvAudioMids(sdp: string, known: ReadonlySet<string>): string[] {
  const out: string[] = [];
  for (const sec of sdp.split(/\r?\n(?=m=)/)) {
    if (!sec.startsWith('m=audio')) continue;
    const mid = /a=mid:([^\r\n]+)/.exec(sec)?.[1];
    if (mid && !known.has(mid) && /\r?\na=recvonly/.test(sec)) out.push(mid);
  }
  return out;
}

/* ----------------------------- what you see now ----------------------------- */

/** A video tile on the page: what it shows, and whether it is on screen now. */
export interface TileView {
  ref: VoiceVideoRef;
  onScreen: boolean;
}

export const refKey = (r: VoiceVideoRef) => `${r.pid}:${r.source}`;
export const sameRef = (a: VoiceVideoRef | null | undefined, b: VoiceVideoRef | null | undefined) =>
  !!a && !!b && a.pid === b.pid && a.source === b.source;

/**
 * The `visible` list (§2.1 rules 1 and 3): nothing while the page or the room
 * is hidden; else the tiles on screen, a screen only while you watch it, the
 * focused one first, at most `VISIBLE_MAX`.
 */
export function visibleRefs(o: {
  shown: boolean;
  tiles: readonly TileView[];
  watching: Readonly<Record<Pid, true>>;
  focus: VoiceVideoRef | null;
}): VoiceVideoRef[] {
  if (!o.shown) return [];
  const on = o.tiles.filter(
    (t) => t.onScreen && (t.ref.source !== 'screen' || !!o.watching[t.ref.pid]),
  );
  const first = on.filter((t) => sameRef(t.ref, o.focus));
  const rest = on.filter((t) => !sameRef(t.ref, o.focus));
  const seen = new Set<string>();
  const out: VoiceVideoRef[] = [];
  for (const t of [...first, ...rest]) {
    const k = refKey(t.ref);
    if (seen.has(k)) continue;
    seen.add(k);
    out.push({ pid: t.ref.pid, source: t.ref.source });
  }
  return out.slice(0, VISIBLE_MAX);
}

export const sameRefs = (a: readonly VoiceVideoRef[], b: readonly VoiceVideoRef[]) =>
  a.length === b.length && a.every((r, i) => sameRef(r, b[i]));

/* ------------------------------ receive slots ------------------------------ */

/** How many receive slots of each kind are worth having now (§2.1 rule 7). */
export function slotsWanted(o: { visible: number; soundStreams: number; deaf: boolean }): {
  video: number;
  audio: number;
} {
  return {
    video: Math.max(0, Math.min(VOICE_VIDEO_SLOTS, o.visible)),
    audio: o.deaf ? 0 : Math.max(0, Math.min(VOICE_STREAM_AUDIO_SLOTS, o.soundStreams)),
  };
}

/**
 * The free slots to remove now: past `wanted`, those free for `lingerMs` or
 * longer (oldest first). A slot in use is never removed.
 */
export function lingerDrop<T extends { pid: Pid | null; freeSince: number }>(
  slots: readonly T[],
  wanted: number,
  now: number,
  lingerMs: number,
): T[] {
  const used = slots.filter((s) => s.pid !== null).length;
  const extra = slots.length - Math.max(wanted, used);
  if (extra <= 0) return [];
  return slots
    .filter((s) => s.pid === null && now - s.freeSince >= lingerMs)
    .sort((a, b) => a.freeSince - b.freeSince)
    .slice(0, extra);
}
